/* screens/kitchen/registries/IngredientsRegistry.jsx — вкладка «Сырьё».
   Реестр закупаемого сырья, группировка по типу. Поиск/фильтр(тип)/сортировка в URL.
   Состояния: loading(SkeletonGrid) / error / 403 / пусто(онбординг) / пусто-выдача(сброс) / список.
   Данные — боевой listIngredients() (lib/api); фильтр/сорт — client-side.
   ХЕНДОФФ → frontend/pages/kitchen/IngredientsTab.jsx. */

import React from 'react';
import { useConfirm } from '../../../app/providers/ConfirmProvider.jsx';
import { useToast } from '../../../app/providers/ToastProvider.jsx';
import { IngredientGridCard, SkeletonGrid } from '../../../components/patterns/Cards.jsx';
import { EmptyState, QueryBoundary } from '../../../components/patterns/States.jsx';
import { KitchenFilterDrawer } from '../../../components/patterns/kitchen/KitchenFilterDrawer.jsx';
import { KitchenToolbar } from '../../../components/patterns/kitchen/KitchenToolbar.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { hooks } from '../../../hooks/index.jsx';
import { useRegistrySelection } from '../../../hooks/useRegistrySelection.jsx';
import { bulkDeleteCards, listIngredients } from '../../../lib/api/index.js';
import { INGREDIENT_CATEGORIES as INGREDIENT_TYPES } from '../../../lib/adapters/ingredients.js';
import { RR } from '../../../lib/router.jsx';

  const { useMemo, useState, useEffect } = React;
  const { useSearchParams } = RR;
  const { useFakeQuery, useDebouncedSearchParam, useDrawerStack } = hooks;

  const SORTS = [
    { value: 'name_asc', label: 'Название А–Я' },
    { value: 'name_desc', label: 'Название Я–А' },
    { value: 'price_asc', label: 'Цена ↑' },
    { value: 'price_desc', label: 'Цена ↓' },
    { value: 'type_asc', label: 'По типу' },
  ];
  const readArr = (p, n) => { const v = p.get(n); return v ? v.split(',') : []; };

  function IngredientsRegistry() {
    const [params, setParams] = useSearchParams();
    const [q, setQ] = useDebouncedSearchParam('q', 300);
    const { push } = useDrawerStack();
    const [filterOpen, setFilterOpen] = useState(false);
    const confirm = useConfirm();
    const toast = useToast();

    const types = readArr(params, 'type');
    const sort = params.get('sort') || 'name_asc';
    const mockErr = params.get('mock_error'); const mock403 = params.get('mock_403');

    const setParam = (name, arr) => setParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (Array.isArray(arr) ? arr.length : arr) sp.set(name, Array.isArray(arr) ? arr.join(',') : arr);
      else sp.delete(name);
      return sp;
    });
    const resetFilters = () => setParams((prev) => {
      const sp = new URLSearchParams(prev); sp.delete('type'); sp.delete('q'); return sp;
    });

    const query = useFakeQuery('ingredients', () => listIngredients(), []);
    useEffect(() => {
      const h = () => query.reload();
      window.addEventListener('kitchen:cards-changed', h);
      return () => window.removeEventListener('kitchen:cards-changed', h);
    }, [query]);

    const groups = useMemo(() => {
      const rows = query.data || [];
      const needle = (q || '').trim().toLowerCase();
      let filtered = rows.filter((it) => {
        if (needle && !it.name.toLowerCase().includes(needle)) return false;
        if (types.length && !types.includes(it.type_label)) return false;
        return true;
      });
      const priceOf = (it) => it.current_price_brief ? it.current_price_brief.amount_rub : -1;
      filtered = [...filtered].sort((a, b) => {
        switch (sort) {
          case 'name_desc': return b.name.localeCompare(a.name, 'ru');
          case 'price_asc': return priceOf(a) - priceOf(b);
          case 'price_desc': return priceOf(b) - priceOf(a);
          case 'type_asc': return a.type_label.localeCompare(b.type_label, 'ru') || a.name.localeCompare(b.name, 'ru');
          default: return a.name.localeCompare(b.name, 'ru');
        }
      });
      // группировка по типу (порядок справочника)
      const order = INGREDIENT_TYPES || [];
      const byType = {};
      filtered.forEach((it) => { (byType[it.type_label] = byType[it.type_label] || []).push(it); });
      const keys = Object.keys(byType).sort((a, b) => {
        const ia = order.indexOf(a), ib = order.indexOf(b);
        return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
      });
      return { list: filtered, keys, byType };
    }, [query.data, q, types.join(','), sort]);

    const hasFilters = types.length > 0 || !!(q && q.trim());

    // F41: множественный выбор (bulk-удаление сырья). resetKey не нужен —
    // вкладка одна; prune по списку защищает от фонового reload.
    const sel = useRegistrySelection(groups.list, {});

    const bulkDelete = async () => {
      const ids = [...sel.selected];
      if (!ids.length) return;
      const ok = await confirm({
        title: `Удалить выбранное сырьё (${ids.length})?`,
        body: `Будет удалено ${ids.length} карточек. Сырьё, используемое в ТТК, будет пропущено. Действие необратимо.`,
        confirmLabel: 'Удалить', tone: 'danger',
      });
      if (!ok) return;
      sel.setBusy(true);
      try {
        let deletedN = 0; let skippedN = 0;
        for (let i = 0; i < ids.length; i += 200) {
          const r = await bulkDeleteCards(ids.slice(i, i + 200));
          if (!r.ok) throw new Error((r.data && r.data.detail) || 'bulk failed');
          deletedN += ((r.data && r.data.deleted) || []).length;
          skippedN += ((r.data && r.data.skipped) || []).length;
        }
        let msg = `Удалено: ${deletedN}`;
        if (skippedN) msg += `; пропущено: ${skippedN} (используются в ТТК)`;
        toast.show(msg, skippedN ? 'accent' : 'success');
        sel.exit();
        query.reload();
        window.dispatchEvent(new CustomEvent('kitchen:cards-changed'));
      } catch (e) {
        toast.show((e && e.message) || 'Не удалось удалить выбранные', 'danger');
      } finally { sel.setBusy(false); }
    };

    // P2-30: пробрасываем type_label (бейдж типа) + supplier_name (чип поставщика);
    // отсутствие цены IngredientGridCard рисует как «Нет цены» (Pill warn), не голым '—'.
    const toCard = (it) => ({
      name: it.name,
      type_label: it.type_label,
      amount_rub: it.current_price_brief ? it.current_price_brief.amount_rub : null,
      unit_label: it.current_price_brief ? it.current_price_brief.unit_label : null,
      supplier_name: it.current_price_brief ? it.current_price_brief.supplier_name : null,
    });

    return (
      <div className="registry">
        {sel.selectMode ? (
          <div className="registry__bulkbar">
            <span className="t-body">Выбрано: {sel.count}</span>
            <Button size="sm" variant="secondary" onClick={sel.selectAll}>
              {sel.allSelected ? 'Снять всё' : 'Выбрать все'}
            </Button>
            <div className="grow" />
            <Button size="sm" variant="danger" iconLeft="trash" disabled={!sel.count || sel.busy} loading={sel.busy} onClick={bulkDelete}>Удалить</Button>
            <Button size="sm" variant="ghost" onClick={sel.exit} disabled={sel.busy}>Готово</Button>
          </div>
        ) : (
          <KitchenToolbar
            search={q} onSearch={setQ} searchPlaceholder="Поиск по сырью…"
            onFilter={() => setFilterOpen(true)} activeFilterCount={types.length}
            sortOptions={SORTS} sortValue={sort} onSort={(v) => setParam('sort', v)}
            actionLabel="Создать ингредиент" onAction={() => push('card', 'new')} />
        )}
        {!sel.selectMode && groups.list.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="sm" variant="ghost" onClick={() => sel.setSelectMode(true)}>Выбрать несколько</Button>
          </div>
        )}

        <QueryBoundary
          loading={query.loading} error={query.error} onRetry={query.reload}
          empty={!query.loading && !query.error && groups.list.length === 0}
          skeleton={<SkeletonGrid count={8} />}
          emptyEl={hasFilters
            ? <EmptyState serif={false} icon="search" title="Ничего не найдено"
                body="По текущему запросу и фильтрам сырьё не найдено."
                action={<Button variant="secondary" onClick={resetFilters}>Сбросить фильтры</Button>} />
            : <EmptyState icon="tag" title="Заведите первый ингредиент"
                body="Сырьё — основа расчётов: КБЖУ, закупочные цены, привязка к ТТК."
                action={<Button variant="primary" iconLeft="plus" onClick={() => push('card', 'new')}>Создать ингредиент</Button>} />}>
          <div className="registry__groups">
            {groups.keys.map((typeLabel) => (
              <section className="registry-group" key={typeLabel}>
                <header className="registry-group__head">
                  <h2 className="registry-group__title t-eyebrow">{typeLabel}</h2>
                  <span className="registry-group__count num">{groups.byType[typeLabel].length}</span>
                </header>
                <div className="tile-grid">
                  {groups.byType[typeLabel].map((it) => (
                    <IngredientGridCard key={it.id} item={toCard(it)} onClick={() => push('card', it.id)}
                      selectable={sel.selectMode} selected={sel.selected.has(it.id)}
                      onToggleSelect={() => sel.toggle(it.id)} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </QueryBoundary>

        <KitchenFilterDrawer
          open={filterOpen} onClose={() => setFilterOpen(false)}
          value={{ type: types }}
          onChange={(d) => setParam('type', d.type || [])}
          sections={[{ key: 'type', title: 'Тип ингредиента',
            options: (INGREDIENT_TYPES || []).map((t) => ({ value: t, label: t })) }]} />
      </div>
    );
  }

export { IngredientsRegistry };
